import { ReactNode, useMemo } from 'react';

import createSafeContext from '#/utils/createSafeContext';
import { logException } from '#/features/logging/logging';
import { useTranslation } from '#/features/localization/utils';

import {
  Locale,
  supportedLocales,
  I18nextProvider,
  i18nInstance,
} from './i18next';

type LocaleView = {
  id: Locale;
  title: string;
};

export interface View {
  readonly locales: LocaleView[];
  readonly activeLocale: LocaleView;
}

export interface Actions {
  readonly changeLocale: (locale: Locale) => void;
}

interface ProviderProps {
  readonly children: ReactNode;
}

export function LocalizationProvider(props: ProviderProps) {
  return (
    <StoreStateProvider>
      <ViewProvider>
        <ActionsProvider>{props.children}</ActionsProvider>
      </ViewProvider>
    </StoreStateProvider>
  );
}

export function StoreStateProvider(props: ProviderProps) {
  return (
    <I18nextProvider i18n={i18nInstance}>{props.children}</I18nextProvider>
  );
}

export const [useLocalesView, LocalesViewProvider] = createSafeContext<View>(
  'LocalesViewProvider',
);

export function ViewProvider(props: ProviderProps) {
  const {
    i18n: { language },
  } = useTranslation();

  const view = useMemo(() => createView(language as Locale), [language]);

  return (
    <LocalesViewProvider value={view}>{props.children}</LocalesViewProvider>
  );
}

export const [useLocaleActions, LocaleActionsProvider] =
  createSafeContext<Actions>('LocaleActions');

export function ActionsProvider(props: ProviderProps) {
  const { i18n } = useTranslation();

  const actions = useMemo(() => createActions(i18n), [i18n]);

  return (
    <LocaleActionsProvider value={actions}>
      {props.children}
    </LocaleActionsProvider>
  );
}

const LOCALE_TITLE_MAP: Record<Locale, string> = {
  en: 'English',
  zh: '中文',
  vi: 'Tiếng Việt',
};

export function createView(activeLocale: Locale): View {
  const localesView = supportedLocales.map((id) => {
    return {
      id,
      title: LOCALE_TITLE_MAP[id],
    };
  });
  const activeLocaleView = {
    id: activeLocale,
    title: LOCALE_TITLE_MAP[activeLocale],
  };

  return {
    activeLocale: activeLocaleView,
    locales: localesView,
  };
}

export function createActions(i18n: typeof i18nInstance): Actions {
  return {
    changeLocale: (locale) => {
      i18n.changeLanguage(locale).catch((cause: unknown) => {
        const message = 'Unknown error changing i18next language';
        const error = new Error(message, { cause });
        logException(error);
      });
    },
  };
}
